import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// ---- Classnames merge ----
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ---- Date Validation ----
export function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

// ---- Date / Time Formatting ----
export function formatDate(date: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValidDate(dateObj)) return 'Invalid Date';
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', ...options }).format(dateObj);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Invalid Date';
  }
}

export function formatTime(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValidDate(dateObj)) return 'Invalid Time';
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(dateObj);
  } catch (error) {
    console.warn('Error formatting time:', error);
    return 'Invalid Time';
  }
}

export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValidDate(dateObj)) return 'Invalid DateTime';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(dateObj);
  } catch (error) {
    console.warn('Error formatting datetime:', error);
    return 'Invalid DateTime';
  }
}

export function getRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  try {
    const now = new Date();
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValidDate(dateObj)) return 'Invalid Date';
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
    return `${Math.floor(diffInSeconds / 31536000)}y ago`;
  } catch (error) {
    console.warn('Error calculating relative time:', error);
    return 'Invalid Date';
  }
}

// ---- Currency / Number Formatting ----
export function formatCurrency(amount: number | null | undefined, currency: string = 'USD', locale: string = 'en-US'): string {
  if (amount === null || amount === undefined || isNaN(amount)) return 'N/A';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    console.warn('Error formatting currency:', error);
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatNumber(number: number | null | undefined, options?: Intl.NumberFormatOptions): string {
  if (number === null || number === undefined || isNaN(number)) return 'N/A';
  try {
    return new Intl.NumberFormat('en-US', { notation: 'compact', compactDisplay: 'short', ...options }).format(number);
  } catch (error) {
    console.warn('Error formatting number:', error);
    return number.toString();
  }
}

export function formatPercentage(value: number | null | undefined, decimals: number = 1): string {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    }).format(value / 100);
  } catch (error) {
    console.warn('Error formatting percentage:', error);
    return `${value.toFixed(decimals)}%`;
  }
}

// ---- String Utilities ----
export function truncateText(text: string | null | undefined, maxLength: number): string {
  if (!text || typeof text !== 'string') return '';
  return text.length <= maxLength ? text : text.slice(0, maxLength).trim() + '...';
}

export function capitalizeFirst(str: string | null | undefined): string {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function slugify(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') return '';
  return text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
}

export function initials(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map(p => p.charAt(0)).join('').toUpperCase();
}

// ---- Array Utilities ----
// (groupBy, sortBy, uniqueBy remain same as in your current implementation)

// ---- Validation Utilities ----
export function isValidEmail(email: string | null | undefined): boolean {
  if (!email || typeof email !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
export function isValidPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone || typeof phone !== 'string') return false;
  return /^\+?[\d\s\-\(\)]{10,}$/.test(phone.trim());
}
export function isValidUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  try { const u = new URL(url); return ['http:', 'https:'].includes(u.protocol); } catch { return false; }
}

// ---- Firebase-specific timestamp handler (UPDATED) ----
export function handleFirestoreTimestamp(timestamp: any): Date | null {
  if (!timestamp) return null;
  try {
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (timestamp instanceof Date) return isValidDate(timestamp) ? timestamp : null;
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isValidDate(date) ? date : null;
    }
    if (timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }
    return null;
  } catch (error) {
    console.warn('Error handling Firestore timestamp:', error);
    return null;
  }
}
